import React, { useRef, useState, useCallback, useEffect, Children, cloneElement } from 'react';

/**
 * KeyboardNavigableList - Wrapper component for lists with keyboard navigation (SAT-87)
 *
 * Features:
 * - Arrow up/down to navigate between items
 * - Home/End to jump to first/last item
 * - Enter to activate the focused item
 * - Maintains a single tab stop (roving tabindex pattern)
 * - Works with any list of focusable children
 *
 * Usage:
 * <KeyboardNavigableList onItemSelect={(index) => handleSelect(index)}>
 *   {items.map((item, index) => (
 *     <KeyboardNavigableList.Item key={item.id} index={index}>
 *       {itemContent}
 *     </KeyboardNavigableList.Item>
 *   ))}
 * </KeyboardNavigableList>
 */
function KeyboardNavigableList({
  children,
  onItemSelect,
  onItemFocus,
  className = '',
  orientation = 'vertical', // 'vertical' | 'horizontal'
  loop = true,
  label,
  ...props
}) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  // Get the count of valid children
  const validChildren = Children.toArray(children).filter(
    child => child && child.type === KeyboardNavigableListItem
  );
  const itemCount = validChildren.length;

  // Reset focused index if it's out of bounds
  useEffect(() => {
    if (focusedIndex >= itemCount && itemCount > 0) {
      setFocusedIndex(itemCount - 1);
    }
  }, [itemCount, focusedIndex]);

  // Notify parent of focus changes
  useEffect(() => {
    if (onItemFocus && focusedIndex >= 0) {
      onItemFocus(focusedIndex);
    }
  }, [focusedIndex, onItemFocus]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (itemCount === 0) return;

    const isVertical = orientation === 'vertical';
    const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';

    switch (e.key) {
      case prevKey:
        e.preventDefault();
        setFocusedIndex(prev => {
          const newIndex = prev <= 0 ? (loop ? itemCount - 1 : 0) : prev - 1;
          // Focus the item
          setTimeout(() => itemRefs.current[newIndex]?.focus(), 0);
          return newIndex;
        });
        break;

      case nextKey:
        e.preventDefault();
        setFocusedIndex(prev => {
          const newIndex = prev >= itemCount - 1 ? (loop ? 0 : itemCount - 1) : prev + 1;
          // Focus the item
          setTimeout(() => itemRefs.current[newIndex]?.focus(), 0);
          return newIndex;
        });
        break;

      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        setTimeout(() => itemRefs.current[0]?.focus(), 0);
        break;

      case 'End':
        e.preventDefault();
        setFocusedIndex(itemCount - 1);
        setTimeout(() => itemRefs.current[itemCount - 1]?.focus(), 0);
        break;

      case 'Enter':
      case ' ':
        // Let the default behavior happen (navigate to link, etc.)
        // But also call the select handler
        if (onItemSelect) {
          onItemSelect(focusedIndex);
        }
        break;

      default:
        break;
    }
  }, [itemCount, loop, onItemSelect, orientation]);

  // Clone children with additional props
  const enhancedChildren = validChildren.map((child, index) => {
    return cloneElement(child, {
      key: child.key || index,
      _index: index,
      _isFocused: index === focusedIndex,
      _setRef: (el) => { itemRefs.current[index] = el; },
      _onFocus: () => setFocusedIndex(index),
      _tabIndex: index === focusedIndex ? 0 : -1
    });
  });

  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label={label}
      aria-activedescendant={`nav-item-${focusedIndex}`}
      onKeyDown={handleKeyDown}
      className={className}
      {...props}
    >
      {enhancedChildren}
    </ul>
  );
}

/**
 * KeyboardNavigableListItem - Individual item in a keyboard-navigable list
 */
function KeyboardNavigableListItem({
  children,
  index,
  className = '',
  as: Component = 'li',
  // Internal props from parent
  _index,
  _isFocused,
  _setRef,
  _onFocus,
  _tabIndex,
  ...props
}) {
  const actualIndex = _index !== undefined ? _index : index;

  // Handle the ref for the focusable element inside
  const handleRef = (el) => {
    if (_setRef) _setRef(el);
  };

  return (
    <Component
      ref={handleRef}
      id={`nav-item-${actualIndex}`}
      role="option"
      aria-selected={_isFocused}
      tabIndex={_tabIndex !== undefined ? _tabIndex : 0}
      onFocus={_onFocus}
      className={`${className} focusable-list-item ${_isFocused ? 'keyboard-focused' : ''}`}
      data-focused={_isFocused}
      {...props}
    >
      {children}
    </Component>
  );
}

// Attach Item as a static property
KeyboardNavigableList.Item = KeyboardNavigableListItem;

export default KeyboardNavigableList;
export { KeyboardNavigableList, KeyboardNavigableListItem };
